import { useEffect, useState } from 'react';

import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';

import IconButton from 'components/@extended/IconButton';
import DrawerHeader from 'components/ui/DrawerHeader';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, clearAllNotifications, deleteNotification } from 'api/enterprise';
import { metallicPalette } from 'themes/colors';
import { HEADER_HEIGHT } from '../../index';

import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

function iconFor(notification) {
  const severity = String(notification.severity || '').toLowerCase();
  if (severity === 'success') return <CheckCircleOutlined />;
  if (severity === 'warning' || severity === 'error') return <ExclamationCircleOutlined />;
  return <MessageOutlined />;
}

function colorFor(notification) {
  const severity = String(notification.severity || '').toLowerCase();
  if (severity === 'success') return { color: '#10B981', bgcolor: alpha('#10B981', 0.12) };
  if (severity === 'warning') return { color: '#F59E0B', bgcolor: alpha('#F59E0B', 0.12) };
  if (severity === 'error') return { color: '#EF4444', bgcolor: alpha('#EF4444', 0.12) };
  return { color: metallicPalette.primary.main, bgcolor: alpha(metallicPalette.primary.main, 0.12) };
}

function timeText(value) {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date);
}

export default function Notification() {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  function loadNotifications() {
    fetchNotifications({ limit: 12 })
      .then((response) => {
        const payload = response?.data || {};
        setRecords(Array.isArray(payload.records) ? payload.records : []);
        setUnreadCount(Number(payload.unreadCount || 0));
      })
      .catch(() => {
        setRecords([]);
        setUnreadCount(0);
      });
  }

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleToggle = () => {
    setOpen((prev) => !prev);
    if (!open) loadNotifications();
  };

  async function markOneRead(notification) {
    if (notification.status === 'Read') return;
    try {
      await markNotificationRead(notification.id);
      setRecords((items) => items.map((item) => item.id === notification.id ? { ...item, status: 'Read' } : item));
      setUnreadCount((count) => Math.max(count - 1, 0));
    } catch (_) {}
  }

  async function markAllRead() {
    try {
      await markAllNotificationsRead();
      setRecords((items) => items.map((item) => ({ ...item, status: 'Read' })));
      setUnreadCount(0);
    } catch (_) {}
  }

  async function deleteAll() {
    try {
      await clearAllNotifications();
      setRecords([]);
      setUnreadCount(0);
    } catch (_) {}
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* Header Notification Icon Button */}
      <Tooltip title="Notifications">
        <IconButton
          color="secondary"
          onClick={handleToggle}
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            color: metallicPalette.grey[7],
            bgcolor: open ? alpha(metallicPalette.primary.main, 0.1) : 'transparent',
            '&:hover': {
              bgcolor: alpha(metallicPalette.primary.main, 0.08),
              color: metallicPalette.primary.main
            }
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <BellOutlined style={{ fontSize: 15 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* ── Slide-Out Notification Right Panel ── */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 5,
          '& .MuiBackdrop-root': {
            top: `${HEADER_HEIGHT}px`
          },
          '& .MuiDrawer-paper': {
            top: `${HEADER_HEIGHT}px !important`,
            height: `calc(100vh - ${HEADER_HEIGHT}px) !important`,
            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px) !important`,
            width: { xs: '100vw', sm: 380 },
            bgcolor: '#FFFFFF',
            boxShadow: '-4px 10px 30px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            m: '0 !important',
            p: '0 !important'
          }
        }}
      >
        {/* Slim Reusable Global Drawer Header (Single Line 42px) */}
        <DrawerHeader
          title="Notifications"
          count={unreadCount}
          countLabel="New"
          onClose={() => setOpen(false)}
          actions={
            <Stack direction="row" spacing={0.75} alignItems="center">
              {/* Delete All (Dustbin Icon in Circle) */}
              <Tooltip title="Delete all">
                <IconButton
                  size="small"
                  onClick={deleteAll}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: '#FEE2E2',
                    color: '#DC2626',
                    border: '1px solid #FCA5A5',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: '#DC2626',
                      color: '#FFFFFF',
                      borderColor: '#DC2626',
                      transform: 'scale(1.08)'
                    }
                  }}
                >
                  <DeleteOutlined style={{ fontSize: 11, fontWeight: 'bold' }} />
                </IconButton>
              </Tooltip>

              {/* Mark as Read (Checkmark Icon in Circle) */}
              <Tooltip title="Mark as read">
                <IconButton
                  size="small"
                  onClick={markAllRead}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: '#E6F4EA',
                    color: '#137333',
                    border: '1px solid #CEEAD6',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: '#137333',
                      color: '#FFFFFF',
                      borderColor: '#137333',
                      transform: 'scale(1.08)'
                    }
                  }}
                >
                  <CheckOutlined style={{ fontSize: 11, fontWeight: 'bold' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />

        {/* Notifications Scrollable List */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List disablePadding>
            {records.map((notification) => {
              const isUnread = notification.status !== 'Read';
              return (
                <ListItem
                  key={notification.id}
                  component={ListItemButton}
                  onClick={() => markOneRead(notification)}
                  sx={{
                    py: 1.8,
                    px: 2.5,
                    borderBottom: `1px solid ${metallicPalette.grey[2]}`,
                    bgcolor: isUnread ? alpha(metallicPalette.primary.main, 0.03) : '#FFFFFF',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(metallicPalette.primary.main, 0.06)
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ ...colorFor(notification), width: 36, height: 36, borderRadius: '10px' }}>
                      {iconFor(notification)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontSize: 13, fontWeight: isUnread ? 800 : 600, color: metallicPalette.grey[9] }}>
                          {notification.title}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: metallicPalette.grey[5], flexShrink: 0 }}>
                          {timeText(notification.createdOn)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Typography sx={{ fontSize: 12, color: metallicPalette.grey[6], mt: 0.3, lineHeight: 1.4 }}>
                        {notification.message}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}

            {!records.length && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <BellOutlined style={{ fontSize: 36, color: metallicPalette.grey[4] }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: metallicPalette.grey[8], mt: 1.5 }}>
                  No Notifications
                </Typography>
                <Typography sx={{ fontSize: 12, color: metallicPalette.grey[5], mt: 0.5 }}>
                  Workflow alerts and system notices will appear here.
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
